import React, { useState, useCallback } from 'react';
import { Upload, Image, Loader2, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useManualAnalysis } from '../hooks/useTradingAnalysis';
import type { AnalysisJobSubmitResponse } from '../types/trading';

const SYMBOLS = [
  'XAUUSD', 'XAGUSD', 'XPTUSD',
  'EURUSD', 'GBPUSD', 'USDJPY', 'AUDUSD', 'USDCHF', 'USDCAD', 'NZDUSD',
];

const TIMEFRAMES = ['M1', 'M5', 'M15', 'M30', 'H1', 'H4', 'D1'];

const TradingManualAnalysis: React.FC = () => {
  const navigate = useNavigate();
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [symbol, setSymbol] = useState('XAUUSD');
  const [timeframe, setTimeframe] = useState('H1');
  const [notes, setNotes] = useState('');
  const [submittedJob, setSubmittedJob] = useState<AnalysisJobSubmitResponse | null>(null);

  const mutation = useManualAnalysis();

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const dropped = e.dataTransfer.files[0];
    if (dropped && dropped.type.startsWith('image/')) {
      setFile(dropped);
      setPreview(URL.createObjectURL(dropped));
    }
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (selected) {
      setFile(selected);
      setPreview(URL.createObjectURL(selected));
    }
  };

  const handleAnalyze = async () => {
    if (!file) return;
    try {
      const data = await mutation.mutateAsync({ file, symbol, timeframe, notes });
      setSubmittedJob(data);
    } catch {
      // error handled by mutation
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate('/trading')}
          className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
        >
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-white">Manual Analysis</h1>
          <p className="text-slate-400 text-sm">Upload a chart screenshot for AI Vision analysis</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upload Section */}
        <div className="space-y-4">
          {/* Drop Zone */}
          <div
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
            className="border-2 border-dashed border-slate-600 rounded-xl p-8 text-center hover:border-teal-500/50 transition-colors cursor-pointer"
            onClick={() => document.getElementById('file-input')?.click()}
          >
            {preview ? (
              <img
                src={preview}
                alt="Chart preview"
                className="max-h-64 mx-auto rounded-lg"
              />
            ) : (
              <>
                <Image size={48} className="mx-auto mb-3 text-slate-600" />
                <p className="text-slate-400 mb-1">
                  Drag & drop chart screenshot here
                </p>
                <p className="text-sm text-slate-500">or click to browse</p>
              </>
            )}
            <input
              id="file-input"
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="hidden"
            />
          </div>

          {/* Options */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-slate-400 mb-1">Symbol</label>
              <select
                value={symbol}
                onChange={(e) => setSymbol(e.target.value)}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-teal-500"
              >
                {SYMBOLS.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">Timeframe</label>
              <select
                value={timeframe}
                onChange={(e) => setTimeframe(e.target.value)}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-teal-500"
              >
                {TIMEFRAMES.map((tf) => (
                  <option key={tf} value={tf}>{tf}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm text-slate-400 mb-1">Notes (optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any additional context..."
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm h-20 resize-none focus:outline-none focus:border-teal-500"
            />
          </div>

          <button
            onClick={handleAnalyze}
            disabled={!file || mutation.isPending}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-teal-500 hover:bg-teal-400 disabled:bg-slate-700 disabled:text-slate-500 text-slate-900 font-semibold rounded-lg transition-colors"
          >
            {mutation.isPending ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Analyzing...
              </>
            ) : (
              <>
                <Upload size={16} />
                Analyze Chart
              </>
            )}
          </button>

          {mutation.isError && (
            <p className="text-red-400 text-sm">
              {mutation.error?.message || 'Analysis failed'}
            </p>
          )}
        </div>

        {/* Result Section */}
        <div>
          <h2 className="text-sm font-medium text-slate-400 mb-3">Analysis Status</h2>
          {submittedJob ? (
            <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-8 text-center space-y-3">
              <CheckCircle2 size={32} className="mx-auto text-teal-400" />
              <p className="text-white font-medium">Analysis Queued</p>
              <p className="text-slate-400 text-sm">
                Job #{submittedJob.job_id} has been submitted. Results will appear on the dashboard when processing completes.
              </p>
              <button
                onClick={() => navigate('/trading')}
                className="text-teal-400 hover:text-teal-300 text-sm"
              >
                Go to Dashboard
              </button>
            </div>
          ) : (
            <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-8 text-center">
              <Upload size={32} className="mx-auto mb-3 text-slate-600" />
              <p className="text-slate-400 text-sm">
                Upload a chart screenshot and click Analyze
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TradingManualAnalysis;
